import React, { FC, useEffect, useRef, useState } from 'react';
import { Rect, Text } from 'react-konva';
import type Konva from 'konva';

import { useVisualSelector } from '../../../reduxHooks';
import { selectors } from '../../../ducks/diffUi';

interface DiffColorType {
  bg: string,
  hoverBg: string,
  textBg: string,
}

export enum DiffRectType {
  CHANGED,
  IGNORED
}

const DIFF_COLORS: Record<DiffRectType, DiffColorType> = {
  [DiffRectType.CHANGED]: {
    textBg: '#FA0EFF',
    bg: '#FA0EFF40',
    hoverBg: '#FA0EFF1A',
  },
  [DiffRectType.IGNORED]: {
    textBg: '#2A5372',
    bg: '#556F8359',
    hoverBg: '#2A53721A',
  },
};

interface RectSize {
  height: number;
  width: number;
}

const STROKE_WIDTH = 1;
const FONT_SIZE = 12;
const BASE_PADDING = 4;
const MIN_RECT_SIZE: RectSize = {
  height: 8,
  width: 8,
};

/**
 * Calculates a minimum visible size for a dimension based on zoom level. Will scale a small
 * element to be at least MIN_RECT_SIZE[dimension].
 * @param rect
 * @param dim
 * @param zoom
 */
const getDimVisibleSize = (rect: RectSize, dim: keyof RectSize, zoom: number) => {
  const zoomedSize = rect[dim] * zoom;
  if (zoomedSize > MIN_RECT_SIZE[dim] || zoom >= 2) {
    return rect[dim];
  }

  const minimumSize = MIN_RECT_SIZE[dim] / zoom;
  return zoomedSize > minimumSize ? zoomedSize : minimumSize;
};

export const DiffRect: FC<DiffRectProps> = (props) => {
  const { rect, type, localZoom, setIsHoveringShape } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [textDims, setTextDims] = useState({
    width: 0,
    height: 0,
  });
  const {
    zoom: syncedZoom,
    syncPan,
    diffSettings: {
      emphasizeDiffs,
      hideDiffs,
    },
  } = useVisualSelector(selectors.getDiffUi);
  const color = DIFF_COLORS[type];
  const text = type === DiffRectType.CHANGED ? 'CHANGED' : 'IGNORED';
  const textRef = useRef<Konva.Text>(null);
  const rectRef = useRef<Konva.Rect>(null);
  const zoom = syncPan ? syncedZoom : localZoom;

  useEffect(() => {
    if (textRef.current) {
      setTextDims(textRef.current?.measureSize(text));
    }
  }, [text, zoom]);

  const PADDING = BASE_PADDING / zoom;

  const sharedProps = {
    height: textDims.height + PADDING * 2,
    width: textDims.width + PADDING * 2,
    x: rect.x,
    y: rect.y,
    opacity: isHovered ? 1 : 0,
  };

  const isTransparentFill = isHovered || (emphasizeDiffs && hideDiffs);
  let fillColor: string | undefined = color.bg;

  if (isTransparentFill) {
    fillColor = 'white';
  } else if (emphasizeDiffs) {
    fillColor = color.textBg;
  } else if (hideDiffs) {
    fillColor = undefined;
  }

  const enabledHovered = () => !hideDiffs && setIsHovered(true);
  const disableHovered = () => !hideDiffs && setIsHovered(false);

  useEffect(() => {
    setIsHoveringShape(isHovered);
  }, [isHovered, setIsHoveringShape]);

  // Resize to make larger / more visible when zoomed far out.
  const rectVisibleSize = {
    height: getDimVisibleSize(rect, 'height', zoom),
    width: getDimVisibleSize(rect, 'width', zoom),
  };

  // Adjust x & y positioning if we've resized the element
  const rectSizeProps = {
    ...rectVisibleSize,
    x: rect.width < rectVisibleSize.width ? (
      rect.x - (rectVisibleSize.width - rect.width) / 2
    ) : rect.x,
    y: rect.height < rectVisibleSize.height ? (
      rect.y - (rectVisibleSize.height - rect.height) / 2
    ) : rect.y,
  };

  return (
    <>
      {/* Stroke - separate element so we can use a different composition type so it's always
      visible */}
      <Rect
        fill="transparent"
        globalCompositeOperation="source-over"
        stroke={isHovered ? color.textBg : undefined}
        strokeWidth={STROKE_WIDTH}
        {...rectSizeProps}
      />
      {/* Actual fill of the rect. Separate layer so we can subtract using destination out when
       using an overlay */}
      <Rect
        fill={fillColor}
        globalCompositeOperation={isTransparentFill ? 'destination-out' : undefined}
        onMouseEnter={enabledHovered}
        onMouseLeave={disableHovered}
        {...rectSizeProps}
      />
      {/* Label background, and label text, respectively. */}
      <Rect
        ref={rectRef}
        cornerRadius={2 / zoom}
        fill={color.textBg}
        fillPatternRepeat="repeat"
        offsetY={textDims.height + PADDING * 2 + STROKE_WIDTH + (
          (rectVisibleSize.height - rect.height) / 2
        )}
        stroke={isHovered ? color.textBg : undefined}
        strokeWidth={1}
        {...sharedProps}
      />
      <Text
        ref={textRef}
        fill="white"
        fontSize={FONT_SIZE / zoom}
        offsetX={-PADDING}
        offsetY={textDims.height + PADDING + STROKE_WIDTH + (
          (rectVisibleSize.height - rect.height) / 2
        )}
        text={text}
        {...sharedProps}
      />
    </>
  );
};

export interface DiffRectProps {
  rect: { width: number, height: number, x: number, y: number };
  type: DiffRectType;
  localZoom: number;
  setIsHoveringShape: (isHovered: boolean) => void;
}
